<template>
  <div
    class="build-and-price-wrapper"
    :class="{
      languageCode,
      'build-and-price-config-flow-wrapper': isConfigFlow,
    }"
  >
    <div v-if="showPaymentInfo" class="smaller-container-wrapper" data-test="paymentInfoHeading">
      <p class="text-dark text-center font-weight-bolder m-0 payment-info">
        {{
          $t('message.eCommerceV2.finance.intro.msg', {
            terms: `${paymentTerm} ${$t('message.eCommerceV2.paymentCalculator.months')}`,
            period: $t(`message.eCommerceV2.paymentCalculator.${paymentPeriod}Period2`),
            paymentAmount: $n(paymentAmount, 'currencyDollars', languageCode),
          })
        }}
      </p>
    </div>
    <MobialisProgressBar
      v-if="$route.meta.navInfo"
      class="pt-2 pt-sm-3 mb-4 pb-1 pb-sm-4 smaller-container-wrapper"
      :class="{ 'mt-0 pt-0': $route.meta.showPaymentInfo }"
      :numberOfSteps="getNumberOfStepsForProgress()"
      :navInfo="$route.meta.navInfo"
    >
    </MobialisProgressBar>
    <template v-if="isConfigFlow">
      <div data-test="configFlowHeading" class="page-heading text-center py-3">
        {{ currentConfigFlowPage.heading }}
      </div>
      <VehicleBuildNavBar :vehicleBuildOptions="configFlow"></VehicleBuildNavBar>
      <div class="smaller-container-wrapper mt-4 pt-sm-3" v-if="!isModelSelect && !isTrimSelect">
        <vehicle-build :showBuildDetails="isPaymentSummary"></vehicle-build>
      </div>
    </template>
    <router-view :class="{ 'with-top-heading': showPaymentInfo }"></router-view>
    <div class="fixed-footer">
      <div v-if="displayNextBtn" class="btn-wrapper" data-test="configFlowBtn">
        <mobialis-button type="button" class="mb-4" @click="$router.push(nextBtnRoute)" data-cy="continue">{{
          nextBtnText
        }}</mobialis-button>
      </div>
      <div v-if="selectedModel" class="vehicle-wrapper">
        <div class="vehicle-wrapper-inner py-2">
          <div class="row align-items-center">
            <div class="col font-weight-bold text-small" data-test="vehicleName">
              {{ footerVehicleName }}
            </div>
            <div class="col-auto text-right">
              <div class="text-smaller text-muted">{{ $t('message.buildAndPrice.footer.buildPrice') }}</div>
              <div class="text-small font-weight-bold" data-test="vehiclePrice">
                {{ $n(footerVehiclePrice, 'currencyDollars', languageCode) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import VehicleBuild from '@/components/build-and-price/VehicleBuild.vue';
import VehicleBuildNavBar from '@/components/build-and-price/VehicleBuildNavBar.vue';

export default {
  name: 'BuildAndPriceWrapper',
  components: {
    VehicleBuild,
    VehicleBuildNavBar,
  },
  data() {
    return {};
  },
  computed: {
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
      paymentAmount: 'paymentCalculator/currentPaymentAmount',
      paymentTerm: 'paymentCalculator/currentPaymentTerm',
      vehiclePrice: 'buildAndPrice/vehiclePrice',
      vehicleName: 'buildAndPrice/vehicleName',
      selectedTrim: 'buildAndPrice/selectedTrim',
      selectedModel: 'buildAndPrice/selectedModel',
      selectedModelPrice: 'buildAndPrice/selectedModelPrice',
      selectedModelName: 'buildAndPrice/selectedModelName',
    }),
    ...mapState('paymentCalculator', ['paymentPeriod', 'paymentMethod']),
    ...mapState('buildAndPrice', ['trims', 'vehicleConfig']),
    configFlow() {
      return [
        {
          name: this.$t('message.buildAndPrice.steps.model.name'),
          heading: this.$t('message.buildAndPrice.steps.model.heading'),
          route: 'model-select',
          isDisabled: false,
        },
        {
          name: this.$t('message.buildAndPrice.steps.trim.name'),
          heading: this.$t('message.buildAndPrice.steps.trim.heading'),
          route: 'trim-select',
          isDisabled: !this.selectedModel || !this.trims,
        },
        {
          name: this.$t('message.buildAndPrice.steps.colour.name'),
          heading: this.$t('message.buildAndPrice.steps.colour.heading'),
          route: 'colours-select',
          isDisabled: !this.selectedTrim || !this.vehicleConfig,
        },
        {
          name: this.$t('message.buildAndPrice.steps.buildSummary.name'),
          heading: this.$t('message.buildAndPrice.steps.buildSummary.heading'),
          route: 'payment-summary',
          isDisabled: !this.vehicleConfig,
        },
      ];
    },
    isConfigFlow() {
      return !!this.$route.meta.isConfigFlow;
    },
    isPaymentSummary() {
      return this.$route.path.includes('/payment-summary');
    },
    isModelSelect() {
      return this.$route.path.includes('/model-select');
    },
    isTrimSelect() {
      return this.$route.path.includes('/trim-select');
    },
    currentConfigFlowIndex() {
      return this.isConfigFlow ? this.configFlow.findIndex(item => this.$route.path.includes(item.route)) : null;
    },
    currentConfigFlowPage() {
      return this.isConfigFlow ? this.configFlow[this.currentConfigFlowIndex] : null;
    },
    nextConfigFlowPage() {
      return this.isConfigFlow && this.$route.meta.hasNext ? this.configFlow[this.currentConfigFlowIndex + 1] : null;
    },
    nextBtnText() {
      return this.nextConfigFlowPage
        ? `${this.$t('message.buildAndPrice.footer.next')} ${this.nextConfigFlowPage.heading}`
        : this.$t('message.buildAndPrice.footer.contactDealer');
    },
    nextBtnRoute() {
      return this.nextConfigFlowPage ? this.nextConfigFlowPage.route : 'contact-dealer/contact-dealer-form';
    },
    displayNextBtn() {
      return (this.nextConfigFlowPage && !this.nextConfigFlowPage.isDisabled) || this.isPaymentSummary;
    },
    // --- Footer vehicle info
    footerVehicleName() {
      return this.vehicleName || this.selectedModelName;
    },
    footerVehiclePrice() {
      return this.vehiclePrice || this.selectedModelPrice;
    },
    // --- Top payment info heading
    showPaymentInfo() {
      return !!(this.$route.meta.showPaymentInfo && this.paymentMethod !== this.enums.PaymentMethod.CASH);
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';

.build-and-price-wrapper {
  padding-bottom: 30px;
}

.build-and-price-config-flow-wrapper {
  padding-bottom: 60px;
}

.payment-info {
  line-height: 13px;
}

.page-heading {
  background: $primary;
  color: white;
  font-weight: 700;
}

img {
  max-width: 100%;
}

// -- Fixed footer

.fixed-footer {
  position: fixed;
  bottom: 0;
  width: 100%;
  right: 0;
  background-color: white;
}

.btn-wrapper {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  padding-right: 15px;
  padding-left: 15px;
}

.fr-CA {
  .btn-wrapper {
    max-width: 800px;
  }
}

.vehicle-wrapper {
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.vehicle-wrapper-inner {
  max-width: 730px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  padding-right: 15px;
  padding-left: 15px;
}

.fr-CA {
  .btn-wrapper {
    max-width: 830px;
  }
}

.text-small {
  font-size: 14px;
  line-height: 18px;
}

.text-smaller {
  font-size: 12px;
  line-height: 18px;
}
</style>
